<template>
	<div>
		<Modal
			:show="showModal"
			@close="closeModal"
			body-classes="m-0 pt-0 pb-0"
			:hideFooter="true"
			footer-classes="pt-0"
			header-classes="pb-1"
			size="lg"
		>
			<template v-slot:header>
				<h5 class="modal-title">{{ headerTitle }}</h5>
			</template>

			<b-overlay :show="isLoading" rounded="sm">
				<div class="pt-2">
					<h5 style="flex: auto; text-align: center">
						Add or edit a product available on the conference checkout page.
					</h5>
					<h6
						v-if="product.errorMessage"
						style="color: red; flex: auto; text-align: center"
					>
						{{ product.errorMessage }}
					</h6>
				</div>

				<form class="pt-1 pb-1">
					<BaseInput
						label="Product Title"
						placeholder="Product Title"
						required
						v-model="product.productTitle"
					/>
					<BaseInput label="Product Description">
						<textarea
							placeholder="Description shown on the cart"
							v-model="product.productDescription"
							class="form-control"
							rows="4"
						/>
					</BaseInput>

					<!-- Conference / dataSet. Options come from
					     account/appInformation.dataSet; value is the single dataSet id
					     (products are one-to-one with a conference; the same offering
					     for two conferences = two products). -->
					<label class="form-control-label">Conference</label>
					<ButtonRadioGroup
						v-if="dataSetOptions.length"
						:options="dataSetOptions"
						v-model="product.dataSet"
					/>
					<small v-else class="form-text text-muted">
						No conferences configured. Add entries under
						<code>account/appInformation.dataSet</code> first.
					</small>

					<b-row class="mt-3">
						<b-col>
							<BaseInput label="Checkout Audience">
								<select
									class="form-control"
									v-model="product.checkoutAudience"
									:disabled="isEdit"
								>
									<option
										v-for="opt in checkoutAudienceOptions"
										:key="opt.value"
										:value="opt.value"
									>
										{{ opt.label }}
									</option>
								</select>
							</BaseInput>
							<small v-if="isEdit" class="form-text text-muted mt--3 mb-3">
								Audience cannot be changed after a product is created.
							</small>
						</b-col>
						<b-col v-if="isExhibitorProduct">
							<BaseInput label="Exhibitor Product Type">
								<select class="form-control" v-model="product.exhibitorProductType">
									<option
										v-for="opt in exhibitorProductTypeOptions"
										:key="opt.value"
										:value="opt.value"
									>
										{{ opt.label }}
									</option>
								</select>
							</BaseInput>
						</b-col>
					</b-row>
					<small v-if="isExhibitorProduct" class="form-text text-muted mb-3">
						Use Gold, Silver, Bronze, or Other for booth packages; the exhibitor's level
						is the highest tier purchased. Use Add-on for sponsorships and extras.
						Exhibitors can combine any items in one order.
					</small>

					<b-row class="mt-3">
						<b-col>
							<CurrencyInput
								label="Price"
								v-model="product.productPrice"
								:value="product.productPrice"
							/>
						</b-col>
						<b-col>
							<CurrencyInput
								label="Discounted Price (optional)"
								v-model="product.discountedPrice"
								:value="product.discountedPrice"
							/>
						</b-col>
					</b-row>

					<!-- Quantity Discounts. Optional flat-tier pricing: once the cart line
					     hits a tier's min quantity, every unit on that line drops to the
					     tier price. Stored on the product doc as `quantityTiers`. -->
					<div class="tier-section">
						<label class="switch-label mb-0">Quantity Discounts (optional)</label>
						<small class="switch-hint d-block mb-2">
							Buyer gets the tier price on every unit once they hit the minimum
							quantity. Each tier price must be below the base price above.
						</small>
						<div
							v-for="(tier, idx) in product.quantityTiers"
							:key="`tier-${idx}`"
							class="tier-row"
						>
							<b-row align-v="end">
								<b-col cols="5">
									<BaseInput
										:label="idx === 0 ? 'Min Quantity' : null"
										placeholder="e.g. 4"
										type="number"
										min="2"
										v-model="tier.minQuantity"
									/>
								</b-col>
								<b-col cols="5">
									<CurrencyInput
										:label="idx === 0 ? 'Tier Price' : null"
										v-model="tier.price"
										:value="tier.price"
									/>
								</b-col>
								<b-col cols="2" class="tier-remove">
									<a
										href="#"
										class="text-danger"
										@click.prevent="removeTier(idx)"
										aria-label="Remove tier"
									>
										Remove
									</a>
								</b-col>
							</b-row>
						</div>
						<a href="#" class="tier-add" @click.prevent="addTier">+ Add tier</a>
					</div>

					<b-row v-if="!isExhibitorProduct">
						<b-col>
							<BaseInput label="Account Level">
								<select
									class="form-control"
									v-model="product.accountLevel"
								>
									<option
										v-for="opt in accountLevelOptions"
										:key="opt.value"
										:value="opt.value"
									>
										{{ opt.label }}
									</option>
								</select>
							</BaseInput>
						</b-col>
						<b-col>
							<BaseInput
								label="Purchases Available (optional)"
								placeholder="Leave blank for unlimited"
								v-model="product.purchasesAvailable"
							/>
						</b-col>
					</b-row>

					<BaseInput
						label="Display Order (higher shows first)"
						placeholder="0"
						v-model="product.index"
					/>

					<b-row align-v="center" class="switch-row">
						<b-col cols="auto" class="mr-auto">
							<label class="switch-label">Allow Multiple Quantities</label>
							<small class="switch-hint">
								Buyer can add more than one of this item to their cart.
							</small>
						</b-col>
						<b-col cols="auto">
							<BaseSwitch v-model="product.allowMultiple" />
						</b-col>
					</b-row>

					<b-row v-if="!isExhibitorProduct" align-v="center" class="switch-row">
						<b-col cols="auto" class="mr-auto">
							<label class="switch-label">Skip Registration</label>
							<small class="switch-hint">
								Hide the attendee registration form for this item — useful for
								add-ons, donations, or vendor booths that don't need named
								attendees.
							</small>
						</b-col>
						<b-col cols="auto">
							<BaseSwitch v-model="product.bypassRegistration" />
						</b-col>
					</b-row>

					<b-row class="mr-0 mt-3 mb-3">
						<div style="flex-grow: 1"></div>
						<base-button
							type="primary"
							:loading="isLoading"
							:shake="shake"
							@click="submit"
						>
							{{ isEdit ? 'Save Changes' : 'Add Product' }}
						</base-button>
					</b-row>
				</form>
			</b-overlay>
		</Modal>
	</div>
</template>
<script>
import { collection, doc, getDoc, setDoc } from '@firebase/firestore';
import { mapGetters } from 'vuex';
import Modal from '../../components/Modals/Modal.vue';
import BaseInput from '../../components/Inputs/BaseInput.vue';
import CurrencyInput from '../../components/Inputs/CurrencyInput.vue';
import BaseSwitch from '../../components/BaseSwitch.vue';
import ButtonRadioGroup from '../../components/ButtonRadioGroup.vue';
import { getOrganizationFirestore } from '../../resources/organizationFirebase';

// Tier labels granted to attendees on purchase. Empty value means no tier
// (the product is then treated as a non-registration add-on).
const ACCOUNT_LEVELS = [
	{ value: '', label: 'None' },
	{ value: 'gold', label: 'Gold' },
	{ value: 'silver', label: 'Silver' },
	{ value: 'bronze', label: 'Bronze' },
];

const CHECKOUT_AUDIENCES = [
	{ value: 'attendee', label: 'Attendee Registration' },
	{ value: 'exhibitor', label: 'Exhibitor Registration' },
];

const EXHIBITOR_PRODUCT_TYPES = [
	{ value: 'gold', label: 'Gold' },
	{ value: 'silver', label: 'Silver' },
	{ value: 'bronze', label: 'Bronze' },
	{ value: 'other', label: 'Other' },
	{ value: 'addon', label: 'Add-on' },
];

const emptyProduct = () => ({
	productTitle: '',
	productDescription: '',
	productPrice: 0,
	discountedPrice: 0,
	accountLevel: '',
	purchasesAvailable: '',
	index: 0,
	allowMultiple: false,
	bypassRegistration: false,
	dataSet: null,
	checkoutAudience: 'attendee',
	exhibitorProductType: null,
	// Optional flat-tier pricing rows. Each entry: { minQuantity, price } in
	// the form's dollar/integer units. Converted to cents on save.
	quantityTiers: [],
	errorMessage: null,
});

export default {
	name: 'NewConferenceProduct',
	components: { Modal, BaseInput, CurrencyInput, BaseSwitch, ButtonRadioGroup },
	props: {
		headerTitle: { type: String, default: 'New Conference Product' },
		showModal: { type: Boolean, default: false },
		editProduct: { type: Object, default: null },
	},
	computed: {
		...mapGetters({ organization: 'getOrganization' }),
		isEdit() {
			return !!(this.editProduct && this.editProduct.productId);
		},
		isExhibitorProduct() {
			return this.product.checkoutAudience === 'exhibitor';
		},
	},
	data() {
		return {
			isLoading: false,
			shake: false,
			accountLevelOptions: ACCOUNT_LEVELS,
			checkoutAudienceOptions: CHECKOUT_AUDIENCES,
			exhibitorProductTypeOptions: EXHIBITOR_PRODUCT_TYPES,
			// Loaded from account/appInformation.dataSet when the modal opens.
			// Shape on Firestore: [{ id, title, imageUrl }, ...]
			// Reshaped to ButtonRadioGroup's {value, name} format below.
			dataSetOptions: [],
			product: emptyProduct(),
		};
	},
	watch: {
		showModal(open) {
			if (open) {
				this.hydrate();
				this.loadDataSets();
			}
		},
	},
	methods: {
		hydrate() {
			if (this.editProduct && this.editProduct.productId) {
				const e = this.editProduct;
				this.product = {
					productTitle: e.productTitle || '',
					productDescription: e.productDescription || '',
					productPrice: e.amount != null ? e.amount / 100 : 0,
					discountedPrice: e.discountedAmount != null ? e.discountedAmount / 100 : 0,
					accountLevel: e.accountLevel != null ? String(e.accountLevel) : '',
					purchasesAvailable:
						e.purchasesAvailable != null ? String(e.purchasesAvailable) : '',
					index: e.index != null ? e.index : 0,
					allowMultiple: e.allowMultiple === true,
					bypassRegistration: e.bypassRegistration === true,
					dataSet: e.dataSet || null,
					checkoutAudience:
						e.checkoutAudience === 'exhibitor' || e.sourceCollection === 'exhibitorProducts'
							? 'exhibitor'
							: 'attendee',
					exhibitorProductType: e.exhibitorProductType || null,
					quantityTiers: Array.isArray(e.quantityTiers)
						? e.quantityTiers.map((t) => ({
							minQuantity: t && t.minQuantity != null ? String(t.minQuantity) : '',
							price: t && t.price != null ? t.price / 100 : 0,
						}))
						: [],
					errorMessage: null,
				};
			} else {
				this.product = emptyProduct();
			}
		},
		addTier() {
			this.product.quantityTiers.push({ minQuantity: '', price: 0 });
		},
		removeTier(idx) {
			this.product.quantityTiers.splice(idx, 1);
		},
		closeModal() {
			this.$emit('update:show', false);
			this.$emit('close');
		},
		// Pull the canonical conference list from account/appInformation.dataSet
		// so the radio group offers the same options the iOS app and other admin
		// views see. Shape on Firestore: [{id, title, imageUrl}, ...].
		async loadDataSets() {
			try {
				const firestore = getOrganizationFirestore(this.organization);
				const snap = await getDoc(doc(firestore, 'account/appInformation'));
				if (!snap.exists()) return;
				const data = snap.data();
				const arr = Array.isArray(data.dataSet) ? data.dataSet : [];
				this.dataSetOptions = arr
					.filter((d) => d && d.id)
					.map((d) => ({ value: d.id, name: d.title || d.id }));
			} catch (e) {
				// Non-fatal — the radio just stays hidden behind its empty-state hint.
			}
		},
		validate() {
			if (
				this.$helpers.isBlank(this.product.productTitle) ||
				this.$helpers.isBlank(this.product.productDescription)
			) {
				return 'Title and description are required.';
			}
			if (this.product.productPrice == null || this.product.productPrice < 0) {
				return 'Price must be zero or greater.';
			}
			if (
				this.isExhibitorProduct &&
				!EXHIBITOR_PRODUCT_TYPES.some(
					(option) => option.value === this.product.exhibitorProductType
				)
			) {
				return 'Select Gold, Silver, Bronze, Other, or Add-on for this exhibitor product.';
			}
			const tiers = Array.isArray(this.product.quantityTiers)
				? this.product.quantityTiers
				: [];
			if (tiers.length > 0) {
				// Base price the tier must beat: discountedPrice if set, else productPrice.
				const basePrice =
					this.product.discountedPrice && this.product.discountedPrice > 0
						? parseFloat(this.product.discountedPrice)
						: parseFloat(this.product.productPrice || 0);
				const seenMins = new Set();
				let prevMin = 1;
				for (let i = 0; i < tiers.length; i++) {
					const tier = tiers[i];
					const minQ = parseInt(tier.minQuantity, 10);
					const price = parseFloat(tier.price);
					if (!Number.isFinite(minQ) || minQ < 2) {
						return `Tier ${i + 1}: minimum quantity must be 2 or higher.`;
					}
					if (!Number.isFinite(price) || price <= 0) {
						return `Tier ${i + 1}: price must be greater than zero.`;
					}
					if (price >= basePrice) {
						return `Tier ${i + 1}: price must be less than the base price.`;
					}
					if (seenMins.has(minQ)) {
						return `Tier ${i + 1}: duplicate minimum quantity (${minQ}).`;
					}
					if (minQ <= prevMin) {
						return `Tiers must be in ascending order of minimum quantity.`;
					}
					seenMins.add(minQ);
					prevMin = minQ;
				}
			}
			return null;
		},
		parseIntOrDefault(value, fallback) {
			const n = parseInt(value, 10);
			return Number.isFinite(n) ? n : fallback;
		},
		submit() {
			this.shake = false;
			const validationError = this.validate();
			if (validationError) {
				this.product.errorMessage = validationError;
				this.shake = true;
				return;
			}
			this.product.errorMessage = null;
			this.isLoading = true;

			const firestore = getOrganizationFirestore(this.organization);
			const productId =
				(this.editProduct && this.editProduct.productId) ||
				doc(collection(firestore, 'products')).id;
			const sourceCollection = this.isExhibitorProduct
				? 'exhibitorProducts'
				: 'products';
			const productRef = doc(firestore, `${sourceCollection}/${productId}`);

			// accountLevel presence is now the proxy for "this is a pass that
			// grants attendee access" (vs. an addon). The backend cart function
			// still keys attendee counting off `productType === 'pass'`, so we
			// derive it here rather than asking the admin to set it explicitly.
			const accountLevel =
				!this.isExhibitorProduct &&
				this.product.accountLevel !== '' && this.product.accountLevel != null
					? String(this.product.accountLevel).trim() || null
					: null;
			const derivedProductType = this.isExhibitorProduct
				? 'addon'
				: accountLevel
					? 'pass'
					: 'addon';

			// Use explicit null for cleared optional fields so a `merge:true` setDoc
			// actually replaces stale values on edit (otherwise omitted keys remain).
			const payload = {
				productId,
				productTitle: this.product.productTitle.trim(),
				productDescription: this.product.productDescription.trim(),
				productType: derivedProductType,
				amount: Math.round(parseFloat(this.product.productPrice || 0) * 100),
				index: this.parseIntOrDefault(this.product.index, 0),
				allowMultiple: this.product.allowMultiple === true,
				// When true, Cart.vue hides the per-attendee registration form
				// for this item (Cart.vue:742 bypassForIndex / AttendeeForm
				// `bypassRegistration` prop). Useful for non-named add-ons,
				// donations, vendor booths.
				bypassRegistration: this.product.bypassRegistration === true,
				discountedAmount:
					this.product.discountedPrice && this.product.discountedPrice > 0
						? Math.round(parseFloat(this.product.discountedPrice) * 100)
						: null,
				// accountLevel is a tier label (e.g. 'gold', 'silver', 'bronze'),
				// not a number. Store the trimmed string so the existing user-
				// provisioning code paths (which match against legacy users with
				// the same string values) continue to work unchanged.
				accountLevel,
				purchasesAvailable:
					this.product.purchasesAvailable !== '' && this.product.purchasesAvailable != null
						? this.parseIntOrDefault(this.product.purchasesAvailable, null)
						: null,
				// Conference assignment. Single-string reference into the
				// account/appInformation.dataSet array. null = unassigned.
				dataSet: this.product.dataSet || null,
				// Tier discount rows in cents, sorted ascending by minQuantity.
				// Empty array (not null) so a `merge:true` setDoc actually clears
				// previously-saved tiers when the admin removes the last row.
				quantityTiers: Array.isArray(this.product.quantityTiers)
					? this.product.quantityTiers
							.map((t) => ({
								minQuantity: parseInt(t.minQuantity, 10),
								price: Math.round(parseFloat(t.price || 0) * 100),
							}))
							.filter(
								(t) =>
									Number.isFinite(t.minQuantity) &&
									t.minQuantity > 0 &&
									Number.isFinite(t.price) &&
									t.price >= 0
							)
							.sort((a, b) => a.minQuantity - b.minQuantity)
					: [],
			};
			if (this.isExhibitorProduct) {
				payload.checkoutAudience = 'exhibitor';
				payload.exhibitorProductType = this.product.exhibitorProductType;
			}

			setDoc(productRef, payload, { merge: true })
				.then(() => {
					this.isLoading = false;
					this.$emit('saved', {
						...payload,
						sourceCollection,
					});
					this.closeModal();
				})
				.catch((error) => {
					this.isLoading = false;
					this.product.errorMessage = error.message;
					this.shake = true;
				});
		},
	},
};
</script>

<style scoped>
/* Toggle row layout — label sits tight above its hint, with breathing room
   below the row before the next input/switch. Mirrors the spacing used on
   other settings panels in the admin. */
.switch-row {
	margin-top: 1.25rem;
	margin-bottom: 0.25rem;
}
.switch-label {
	display: block;
	margin: 0;
	font-weight: 600;
	font-size: 0.875rem;
	color: #32325d;
	line-height: 1.3;
}
.switch-hint {
	display: block;
	margin-top: 0.125rem;
	font-size: 0.75rem;
	color: #8898aa;
	line-height: 1.35;
}
.tier-section {
	margin-top: 1rem;
	margin-bottom: 0.5rem;
}
.tier-row {
	margin-bottom: 0.25rem;
}
.tier-remove {
	font-size: 0.8rem;
	padding-bottom: 0.75rem;
}
.tier-add {
	display: inline-block;
	font-size: 0.85rem;
	margin-top: 0.25rem;
}
</style>
